import { DataDrivenPropertyValueSpecification } from "maplibre-gl";

import { KEY_OPTIONS } from "./get-feature-place-name";

/**
 * Creates a MapLibre expression that builds a label by concatenating multiple feature properties.
 *
 * HOW THIS WORKS:
 * ===============
 *
 * MapLibre uses "expressions" - a JSON-based mini-language that runs on the GPU to style features.
 * Instead of JavaScript loops, we describe the logic declaratively using arrays.
 *
 * BASIC EXPRESSION SYNTAX:
 * ------------------------
 * ["operator", arg1, arg2, ...]
 *
 * Common operators:
 * - ["get", "propertyName"] - Gets a property value from the feature
 * - ["coalesce", value1, value2, ...] - Returns the first non-null value
 * - ["concat", str1, str2, ...] - Concatenates strings
 * - ["case", condition1, output1, condition2, output2, ..., fallback] - If-else logic
 * - ["all", condition1, condition2, ...] - Logical AND
 * - ["any", condition1, condition2, ...] - Logical OR
 * - ["!=", value1, value2] - Not equal comparison
 *
 * EXAMPLE WALKTHROUGH:
 * --------------------
 * Given properties: { gemeentenaam: "Amsterdam", buurtnaam: "Centrum", naam: "Dam" }
 * Given keyOptions: ["gemeentenaam", "buurtnaam", "naam"]
 *
 * We want to build: "Amsterdam, Centrum, Dam"
 *
 * The expression works like this:
 *
 * 1. Start with the first property:
 *    ["coalesce", ["get", "gemeentenaam"], ""]  →  "Amsterdam"
 *
 * 2. Add a separator ONLY IF both the previous part and the next part exist:
 *    ["case",
 *      ["all",
 *        ["!=", ["coalesce", ["get", "gemeentenaam"], ""], ""],  ← previous exists?
 *        ["!=", ["coalesce", ["get", "buurtnaam"], ""], ""]      ← next exists?
 *      ],
 *      ", ",  ← if both exist, add ", "
 *      ""     ← otherwise add nothing
 *    ]
 *
 * 3. Add the next property:
 *    ["coalesce", ["get", "buurtnaam"], ""]  →  "Centrum"
 *
 * 4. Repeat steps 2-3 for all remaining properties
 *
 * 5. Wrap everything in ["concat", ...] to join all parts:
 *    ["concat", "Amsterdam", ", ", "Centrum", ", ", "Dam"]  →  "Amsterdam, Centrum, Dam"
 *
 * EDGE CASES HANDLED:
 * -------------------
 * - If gemeentenaam is missing: "Centrum, Dam" (no leading separator)
 * - If buurtnaam is missing: "Amsterdam, Dam" (no double separators)
 * - If all are missing: "" (empty string)
 * - If only one exists: "Amsterdam" (no trailing separator)
 *
 * @param keyOptions - Array of property names to concatenate (e.g., ["gemeentenaam", "buurtnaam", "naam"])
 * @param separator - String to use between values (default: ", ")
 * @returns MapLibre expression that can be used in layout["text-field"]
 */
const createFeatureLabelExpression = (): DataDrivenPropertyValueSpecification<string> => {
  // Build the concat expression parts
  const concatParts: unknown[] = [];

  KEY_OPTIONS.forEach((key, index) => {
    // Add the property value (or empty string if it doesn't exist)
    concatParts.push(["coalesce", ["get", key], ""]);

    // Add a separator ONLY if:
    // 1. This is not the last property
    // 2. At least one previous property has a value
    // 3. The next property has a value
    if (index < KEY_OPTIONS.length - 1) {
      // Build condition: "does any previous property have a value?"
      const previousPropertiesExist =
        index === 0
          ? ["!=", ["coalesce", ["get", KEY_OPTIONS[0]], ""], ""] // For first item, just check itself
          : [
              "any",
              ...KEY_OPTIONS.slice(0, index + 1).map((prevKey) => [
                "!=",
                ["coalesce", ["get", prevKey], ""],
                "",
              ]),
            ];

      // Build condition: "does the next property have a value?"
      const nextPropertyExists = ["!=", ["coalesce", ["get", KEY_OPTIONS[index + 1]], ""], ""];

      // Add separator only if BOTH conditions are true
      concatParts.push(["case", ["all", previousPropertiesExist, nextPropertyExists], ", ", ""]);
    }
  });

  // Wrap everything in a concat expression
  return ["concat", ...concatParts] as DataDrivenPropertyValueSpecification<string>;
};

export const FEATURE_LABEL_EXPRESSION = createFeatureLabelExpression();
